import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from '../../firebase';
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"
import './../../css/NotebookSettings.css';

function NotebookSettings() {
    const [globalPalmSettings, setGlobalPalmSettings] = useState({
        enabled: false,
        sensitivity: 'medium',
        inputMode: 'auto',
        edgeRejection: true,
        multiTouchFiltering: true,
        showDebug: false
    });
    
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const { enhancedRightClickEnabled } = useQuickLinks();

    const navigate = useNavigate();

    // Custom hook to track user and uid reactively
    function useCurrentUserUid() {
        const [user, setUser] = useState(() => auth.currentUser);
        const [uid, setUid] = useState(() => auth.currentUser ? auth.currentUser.uid : null);
        useEffect(() => {
            const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
                setUser(firebaseUser);
                setUid(firebaseUser ? firebaseUser.uid : null);
            });
            return () => unsubscribe();
        }, []);
        return [user, uid];
    }

    const [user, userUid] = useCurrentUserUid();

    // Load global settings when component mounts
    useEffect(() => {
        loadGlobalSettings();
    }, []);

    const loadGlobalSettings = async () => {
        try {
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                // Try to load from localStorage as fallback
                const storedPalm = localStorage.getItem('globalPalmSettings');
                if (storedPalm) {
                    setGlobalPalmSettings(JSON.parse(storedPalm));
                }
                return;
            }

            // Load from backend API
            const response = await fetch(
                'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/user/preferences/',
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'requesting-user-uid': user.uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.preferences) {
                    if (data.preferences.palmRejection) {
                        setGlobalPalmSettings(data.preferences.palmRejection);
                        localStorage.setItem('globalPalmSettings', JSON.stringify(data.preferences.palmRejection));
                    }
                }
            } else {
                // Fallback to localStorage if API fails
                const storedPalm = localStorage.getItem('globalPalmSettings');
                if (storedPalm) {
                    setGlobalPalmSettings(JSON.parse(storedPalm));
                }
            }
            
        } catch (error) {
            console.error('Error loading global settings:', error);
            // Fallback to localStorage on error
            const storedPalm = localStorage.getItem('globalPalmSettings');
            if (storedPalm) {
                setGlobalPalmSettings(JSON.parse(storedPalm));
            }
        }
    };

    const saveGlobalSettings = async (newSettings) => {
        setLoading(true);
        setSaveStatus('');

        try {
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                setSaveStatus('error');
                setTimeout(() => setSaveStatus(''), 3000);
                return;
            }

            // Prepare the preferences object with palm rejection settings only
            const preferences = {
                palmRejection: newSettings
            };

            // Save to backend API
            const response = await fetch(
                'https://bluebirddocumentationadmin.pythonanywhere.com/notebooks/user/preferences/',
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'requesting-user-uid': user.uid,
                    },
                    body: JSON.stringify({
                        preferences
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.preferences && data.preferences.palmRejection) {
                    setGlobalPalmSettings(data.preferences.palmRejection);
                    localStorage.setItem('globalPalmSettings', JSON.stringify(data.preferences.palmRejection));
                } else {
                    // Fallback to local settings
                    setGlobalPalmSettings(newSettings);
                    localStorage.setItem('globalPalmSettings', JSON.stringify(newSettings));
                }
                setSaveStatus('success');
            } else {
                const errorData = await response.json();
                console.error('API Error:', errorData);
                // Fallback to localStorage only
                localStorage.setItem('globalPalmSettings', JSON.stringify(newSettings));
                setGlobalPalmSettings(newSettings);
                setSaveStatus('error');
            }
            
            setTimeout(() => setSaveStatus(''), 3000);
            
        } catch (error) {
            console.error('Error saving global settings:', error);
            // Fallback to localStorage on error
            localStorage.setItem('globalPalmSettings', JSON.stringify(newSettings));
            setGlobalPalmSettings(newSettings);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handlePalmSettingChange = (setting, value) => {
        const newSettings = {
            ...globalPalmSettings,
            [setting]: value
        };
        setGlobalPalmSettings(newSettings);
        saveGlobalSettings(newSettings);
    };

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
  {
    label: "Your Documents",
    url: "/documentation",
    onClick: () => navigate("/documentation"),
  },
  {
    label: "Your Walkthroughs",
    url: "/walkthroughs",
    onClick: () => navigate("/walkthroughs"),
  },
  {
    label: "Your Notebook",
    url: "/my-notebook",
    onClick: () => navigate("/my-notebook"),
  },
  {
    label: "Your Workspace",
    url: "/workspaces",
    onClick: () => navigate("/workspaces"),
  },
  {
    label: "Account Settings",
    url: `/account/${userUid}`,
    onClick: () => navigate(`/account/${userUid}`),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    if (!user) {
        return (
            <div 
                className="DocumentationMainPage__div"
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
            >
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>To configure Notebook Settings, you must have an account with us</strong>. If you already have an account, 
                        you can login by clicking the Login button below. If you don't already have an account, you can create a new 
                        one by clicking the Register button below.
                    </p>
                    <button onClick={() => navigate('/')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Home</button>
                    <button onClick={() => navigate('/login')} style={{ marginRight: "10px" }} className="ConflictResolver__button">Login</button>
                    <button onClick={() => navigate('/register')} className="ConflictResolver__button">Register</button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="notebook-settings"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >

            <button className="NotebookSettings__back-arrow-btn" onClick={() => window.history.back()}>
                <i className="fas fa-arrow-left"></i>
            </button>
        
            <div className="settings-header">
                <h2>Notebook Settings</h2>
                <p>Configure your notebook and drawing preferences.</p>
                
                {saveStatus && (
                    <div className={`save-status ${saveStatus}`}>
                        {saveStatus === 'success' && (
                            <><i className="fas fa-check-circle"></i> Settings saved successfully!</>
                        )}
                        {saveStatus === 'error' && (
                            <><i className="fas fa-exclamation-circle"></i> Failed to save settings. Please try again.</>
                        )}
                    </div>
                )}
            </div>
            
            <div className="settings-content">
                <div className="settings-section">
                    <h3>Palm Rejection Settings</h3>
                    <p>Configure palm rejection settings that will apply to all notebooks when drawing. These settings are global and will be used whenever you're drawing in any notebook.</p>
                    
                    <div className="palm-settings-grid">
                        <div className="setting-group">
                            <div className="setting-with-toggle">
                                <span className={`setting-label-text ${loading ? 'disabled' : ''}`}>
                                    Enable Palm Rejection by Default
                                </span>
                                <label className="Create__switch">
                                    <input
                                        type="checkbox"
                                        checked={globalPalmSettings.enabled}
                                        onChange={(e) => handlePalmSettingChange('enabled', e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="NotebookSettings__slider round"></span>
                                </label>
                            </div>
                        </div>

                        <div className="setting-group">
                            <label className="setting-label">Input Mode</label>
                            <select
                                value={globalPalmSettings.inputMode}
                                onChange={(e) => handlePalmSettingChange('inputMode', e.target.value)}
                                className="setting-select"
                                disabled={loading}
                            >
                                <option value="auto">Auto-detect</option>
                                <option value="finger">Finger mode</option>
                                <option value="stylus">Stylus mode</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <label className="setting-label">Sensitivity</label>
                            <select
                                value={globalPalmSettings.sensitivity}
                                onChange={(e) => handlePalmSettingChange('sensitivity', e.target.value)}
                                className="setting-select"
                                disabled={loading}
                            >
                                <option value="low">Low (less aggressive)</option>
                                <option value="medium">Medium (recommended)</option>
                                <option value="high">High (more aggressive)</option>
                                <option value="very-high">Very High (maximum rejection)</option>
                            </select>
                        </div>

                        <div className="setting-group">
                            <div className="setting-with-toggle">
                                <span className={`setting-label-text ${loading ? 'disabled' : ''}`}>
                                    Edge Rejection
                                </span>
                                <label className="Create__switch">
                                    <input
                                        type="checkbox"
                                        checked={globalPalmSettings.edgeRejection}
                                        onChange={(e) => handlePalmSettingChange('edgeRejection', e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="NotebookSettings__slider round"></span>
                                </label>
                            </div>
                            <small className="setting-description">Ignore touches near screen edges</small>
                        </div>

                        <div className="setting-group">
                            <div className="setting-with-toggle">
                                <span className={`setting-label-text ${loading ? 'disabled' : ''}`}>
                                    Multi-touch Filtering
                                </span>
                                <label className="Create__switch">
                                    <input
                                        type="checkbox"
                                        checked={globalPalmSettings.multiTouchFiltering}
                                        onChange={(e) => handlePalmSettingChange('multiTouchFiltering', e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="NotebookSettings__slider round"></span>
                                </label>
                            </div>
                            <small className="setting-description">Filter out secondary touches when drawing</small>
                        </div>

                        <div className="setting-item">
                            <div className="setting-with-toggle">
                                <span className={`setting-label-text ${loading ? 'disabled' : ''}`}>
                                    Debug Mode
                                </span>
                                <label className="Create__switch">
                                    <input
                                        type="checkbox"
                                        checked={globalPalmSettings.showDebug}
                                        onChange={(e) => handlePalmSettingChange('showDebug', e.target.checked)}
                                        disabled={loading}
                                    />
                                    <span className="NotebookSettings__slider round"></span>
                                </label>
                            </div>
                            <small className="setting-description">Show technical details about touch detection</small>
                        </div>
                    </div>
                </div>
                
                <div className="settings-section">
                    <h3>How Palm Rejection Works</h3>
                    <p>Palm rejection helps prevent accidental touches when drawing with your finger or stylus. The settings above apply to all your notebooks.</p>
                    
                    <div className="settings-info">
                        <h4>Tips for best results:</h4>
                        <ol>
                            <li>Use a stylus for more precise drawing and better palm detection</li>
                            <li>Adjust sensitivity based on how often you experience false touches</li>
                            <li>Enable edge rejection if you often accidentally touch screen edges</li>
                            <li>Multi-touch filtering helps when you rest multiple fingers while drawing</li>
                        </ol>
                    </div>
                </div>
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default NotebookSettings;